import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { createMediaRecorderCamera, type CameraInstance } from '@/utils/MediaRecorderCamera'
import { studySessionService } from '@/services/studySessionService'
import { useAuthStore } from './authStore'
import { easybackClient } from '@/services/api'
import { API_BASE } from '@/services/config'

type SessionContext = 'lesson' | 'quiz' | 'dashboard' | 'practice' | 'other' | null

interface SessionState {
  sessionId: number | null
  userId: number | null
  lessonId: number | null
  context: SessionContext
  startTime: number | null
  durationSeconds: number
  isPaused: boolean
  isRunning: boolean
  isCameraActive: boolean
  snapshotCount: number
  lastActivity: number
  isIdle: boolean
  error: string | null
  isHydrated: boolean

  _intervalRef: ReturnType<typeof setInterval> | null
  _cameraInstance: CameraInstance | null
  _lastAutoSave: number

  startSession: (userId: number, lessonId: number | null, context?: SessionContext) => Promise<void>
  pauseSession: () => void
  resumeSession: () => void
  endSession: (notes?: string) => Promise<void>
  updateContext: (context: SessionContext) => Promise<void>
  updateActivity: () => void
  clearIdle: () => void
  resetSession: () => void
  setError: (error: string | null) => void
  initializeFromPersisted: () => Promise<void>

  _startTimer: () => void
  _stopTimer: () => void
  _tick: () => void
  _autoSave: () => Promise<void>
  _startCamera: () => Promise<void>
  _stopCamera: () => void
  _handleCameraCapture: (imageData: string, _sessionId: number) => Promise<void>
}

const initialState = {
  sessionId: null,
  userId: null,
  lessonId: null,
  context: null,
  startTime: null,
  durationSeconds: 0,
  isPaused: false,
  isRunning: false,
  isCameraActive: false,
  snapshotCount: 0,
  lastActivity: Date.now(),
  isIdle: false,
  error: null,
  isHydrated: false,
  _intervalRef: null,
  _cameraInstance: null,
  _lastAutoSave: 0
}

const AUTO_SAVE_INTERVAL = 60
const IDLE_TIMEOUT_SECONDS = 120

export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      ...initialState,

      initializeFromPersisted: async () => {
        const { sessionId, isRunning, isPaused, isCameraActive, userId } = get()

        set({ isHydrated: false })

        if (!sessionId) {
          set({ isHydrated: true })
          return
        }

        get()._stopTimer()
        get()._stopCamera()

        let currentUser = useAuthStore.getState().user

        const maxWait = 3000
        const startTime = Date.now()

        while (!currentUser && Date.now() - startTime < maxWait) {
          await new Promise(resolve => setTimeout(resolve, 50))
          currentUser = useAuthStore.getState().user
        }

        if (currentUser && userId !== currentUser.id) {
          get()._stopTimer()
          get()._stopCamera()
          set({ ...initialState, isHydrated: true })
          return
        }

        if (isRunning && !isPaused) {
          try {
            get()._startTimer()
            if (isCameraActive) {
              await get()._startCamera()
            }
            set({ isHydrated: true })
          } catch (error) {
            console.error('Failed to rehydrate session:', error)
            get()._stopTimer()
            get()._stopCamera()
            set({ isRunning: false, isPaused: false, isCameraActive: false, isHydrated: true })
          }
        } else if (isPaused) {
          get()._stopTimer()
          get()._stopCamera()
          set({ isHydrated: true })
        } else {
          set({ isHydrated: true })
        }
      },

      startSession: async (userId, lessonId, context = 'other') => {
        set({ error: null })

        try {
          const session = await studySessionService.logSession({
            user_id: userId,
            lesson_id: lessonId ?? 0,
            notes: '',
            context
          })

          set({
            sessionId: session.id,
            userId,
            lessonId,
            context,
            startTime: Date.now(),
            durationSeconds: 0,
            isPaused: false,
            isRunning: true,
            isCameraActive: false,
            snapshotCount: 0,
            lastActivity: Date.now(),
            _lastAutoSave: 0,
            error: null
          })

          get()._startTimer()
          await get()._startCamera()
        } catch (error) {
          set({ error: 'Oturum başlatılamadı' })
          throw error
        }
      },

      pauseSession: () => {
        const { isRunning, isPaused } = get()
        if (!isRunning || isPaused) return

        get()._stopTimer()
        get()._stopCamera()

        set({
          isPaused: true,
          isCameraActive: false,
          lastActivity: Date.now()
        })
      },

      resumeSession: () => {
        const { isRunning, isPaused } = get()
        if (!isRunning || !isPaused) return

        set({
          isPaused: false,
          lastActivity: Date.now()
        })

        get()._startTimer()
        get()._startCamera().catch(console.error)
      },

      endSession: async (notes) => {
        const { sessionId, durationSeconds } = get()

        get()._stopTimer()
        get()._stopCamera()

        if (!sessionId) {
          set(initialState)
          return
        }

        try {
          await studySessionService.endSession(sessionId, {
            duration: Math.floor(durationSeconds / 60),
            completed: true,
            notes: notes || ''
          })
        } catch (error) {
          console.error('Failed to end session:', error)
        }

        set(initialState)
      },

      updateContext: async (context) => {
        const { sessionId, context: currentContext } = get()
        if (!sessionId || currentContext === context) return

        try {
          await studySessionService.updateSession(sessionId, { context })
          set({ context, lastActivity: Date.now() })
        } catch {
          set({ error: 'Oturum bağlamı güncellenemedi' })
        }
      },

      updateActivity: () => {
        const { isRunning, isPaused } = get()
        if (isRunning && !isPaused) {
          set({ lastActivity: Date.now(), isIdle: false })
        }
      },

      clearIdle: () => {
        const { isRunning, isPaused } = get()
        if (isRunning && isPaused) {
          get().resumeSession()
        }
        set({ isIdle: false, lastActivity: Date.now() })
      },

      resetSession: () => {
        get()._stopTimer()
        get()._stopCamera()
        set(initialState)
      },

      setError: (error) => set({ error }),

      _startTimer: () => {
        const { _intervalRef } = get()
        if (_intervalRef) return true

        try {
          const intervalRef = setInterval(() => {
            get()._tick()
          }, 1000)

          set({ _intervalRef: intervalRef })
          return true
        } catch {
          return false
        }
      },

      _stopTimer: () => {
        const { _intervalRef } = get()
        if (_intervalRef) {
          clearInterval(_intervalRef)
          set({ _intervalRef: null })
        }
      },

      _tick: () => {
        const { durationSeconds, isPaused, lastActivity } = get()

        if (isPaused) return

        const now = Date.now()
        const idleTime = now - lastActivity

        if (idleTime > IDLE_TIMEOUT_SECONDS * 1000) {
          set({ isIdle: true })
          get().pauseSession()
          return
        }

        const newDuration = durationSeconds + 1
        set({ durationSeconds: newDuration })

        if (newDuration - get()._lastAutoSave >= AUTO_SAVE_INTERVAL) {
          set({ _lastAutoSave: newDuration })
          get()._autoSave().catch((err) => {
            console.error('Failed to auto-save session:', err)
          })
        }
      },

      _autoSave: async () => {
        const { sessionId, durationSeconds } = get()
        if (!sessionId) return

        const durationMinutes = Math.floor(durationSeconds / 60)
        await studySessionService.updateSessionDuration(sessionId, durationMinutes)
      },

      _startCamera: async () => {
        const { _cameraInstance, sessionId, userId } = get()

        if (!sessionId || !userId) return

        if (_cameraInstance) {
          _cameraInstance.startAutoCapture()
          set({ isCameraActive: true })
          return
        }

        try {
          const cameraInstance = createMediaRecorderCamera({
            width: 320,
            height: 240,
            interval: 60,
            onCapture: (imageData: string) => get()._handleCameraCapture(imageData, sessionId)
          })

          const started = await cameraInstance.startCamera()
          if (started) {
            cameraInstance.startAutoCapture()
            set({ _cameraInstance: cameraInstance, isCameraActive: true })
          }
        } catch (error) {
          console.error('Failed to start camera:', error)
        }
      },

      _stopCamera: () => {
        const { _cameraInstance } = get()
        if (_cameraInstance) {
          _cameraInstance.stopAutoCapture()
          _cameraInstance.stopCamera()
        }
        set({ isCameraActive: false, _cameraInstance: null })
      },

      _handleCameraCapture: async (imageData: string, captureSessionId: number) => {
        const { sessionId, userId, snapshotCount } = get()

        if (!sessionId || sessionId !== captureSessionId || !userId) return

        try {
          const r2Key = `study-snapshots/${userId}/${sessionId}/${Date.now()}.jpg`

          const formData = new FormData()
          formData.append('file', dataURLtoBlob(imageData), 'snapshot.jpg')
          formData.append('table', 'study_sessions')
          formData.append('name', 'snapshot.jpg')
          formData.append('recordId', sessionId.toString())
          formData.append('field', 'image')
          formData.append('metadata', JSON.stringify({
            user_id: userId,
            timestamp: Date.now().toString()
          }))

          const uploadResponse = await fetch(API_BASE + '/storage/upload', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${useAuthStore.getState().token}`
            },
            body: formData
          })

          if (!uploadResponse.ok) throw new Error('Upload failed')

          const uploadResult = await uploadResponse.json()

          await easybackClient.insert('study_snapshots', {
            session_id: sessionId,
            user_id: userId,
            image_url: uploadResult.data.url,
            r2_key: r2Key,
            timestamp: Date.now(),
            is_flagged: 0
          })

          set({ snapshotCount: snapshotCount + 1 })

          await studySessionService.updateSnapshotCount(sessionId)
        } catch (error) {
          console.error('Failed to save snapshot:', error)
        }
      }
    }),
    {
      name: 'session-storage',
      partialize: (state) => ({
        sessionId: state.sessionId,
        userId: state.userId,
        lessonId: state.lessonId,
        context: state.context,
        startTime: state.startTime,
        durationSeconds: state.durationSeconds,
        isPaused: state.isPaused,
        isRunning: state.isRunning,
        isCameraActive: state.isCameraActive,
        snapshotCount: state.snapshotCount,
        lastActivity: state.lastActivity,
        isIdle: state.isIdle,
        isHydrated: state.isHydrated
      })
    }
  )
)

export const useActiveSession = () => {
  const session = useSessionStore()
  const user = useAuthStore((state) => state.user)

  return {
    isActive: session.sessionId !== null && session.userId === user?.id && session.isRunning && session.isHydrated,
    isPaused: session.isPaused,
    duration: session.durationSeconds,
    isCameraActive: session.isCameraActive,
    snapshotCount: session.snapshotCount,
    context: session.context,
    isIdle: session.isIdle,
    isHydrated: session.isHydrated
  }
}

export const useSessionTimer = () => {
  const session = useSessionStore()

  return {
    start: session.startSession,
    pause: session.pauseSession,
    resume: session.resumeSession,
    end: session.endSession,
    updateContext: session.updateContext,
    updateActivity: session.updateActivity,
    clearIdle: session.clearIdle,
    reset: session.resetSession,
    isHydrated: session.isHydrated
  }
}

function dataURLtoBlob(dataURL: string): Blob {
  const parts = dataURL.split(',')
  const mime = parts[0].match(/:(.*?);/)?.[1] ?? 'image/jpeg'
  const bstr = atob(parts[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }

  return new Blob([u8arr], { type: mime })
}
